/**
 * How the cluster index is scored — at the BOTTOM of the Cluster View, collapsed.
 *
 * ⚠️ IT USED TO SIT DIRECTLY UNDER THE CARDS (Theresa, Sep 29 2026: move the explanations "to a
 * collapsed details section at the bottom of the page"). Eight lines of prose between the three
 * cards and the work is eight lines nobody reads twice and everybody scrolls past every week — the
 * same rule Planning already follows, where every explanation lives behind the ⓘ beside the thing
 * it explains. Nothing was cut: every sentence, weight and threshold below is the text that was
 * there, and it is still computed from the live config rather than transcribed.
 *
 * ⚠️ IT RECOMPUTES `computeIndexes` rather than being handed the result. That is one extra pass
 * over three clusters, and it buys the property that matters here: the two claims this section
 * makes about the scoring — that capacity was adjusted, and which criteria were excluded — come
 * from the same function the cards are drawn from, so they cannot describe a different calculation
 * from the one on screen.
 */
import { useMemo } from 'react';
import type { CapacityState, CheckInData } from '../../../types/taCheckin';
import { CRITERIA, computeIndexes } from '../../../utils/taCheckinIndex';

export function IndexExplainer({ data, state }: { data: CheckInData; state: CapacityState }) {
  const cfg = data.config;
  const list = useMemo(() => computeIndexes(data, state), [data, state]);
  const missing = [...new Set(list.flatMap((l) => l.missing.map((m) => m.label)))];
  const anyOut = list.some((l) => l.cap.lost > 0);

  return (
    <details className="group rounded-lg border border-gray-200 bg-white">
      <summary className="flex cursor-pointer list-none items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
        <span
          aria-hidden
          className="inline-block h-2 w-2 border-r-[1.5px] border-b-[1.5px] border-gray-400 transition-transform duration-150 group-open:rotate-45"
          style={{ transform: 'rotate(-45deg)' }}
        />
        How the cluster index is scored
      </summary>

      <p className="border-t border-gray-100 px-4 py-3 text-xs leading-relaxed text-gray-500">
        Every criterion is scored as a percentage of the average across clusters, so{' '}
        <b>100 = average</b>. Weighted:{' '}
        {CRITERIA.map((c) => `${c.label.toLowerCase()} ${cfg.index_weights?.[c.id] ?? 0}%`).join(', ')}.
        Role load and interviews are per available FTE so different team sizes compare fairly.{' '}
        <b>Pipeline risk</b> is the share of a cluster&apos;s open postings that are stalled — nobody
        at or past first interview, or {data.pipeline.stuckThreshold ?? 3}+ candidates without a
        stage move in 14 days; it measures whether pipelines are moving. <b>Pipeline volume</b> is
        the median candidates per open posting and is scored <b>inverted</b> — fewer candidates
        scores higher, because thin pipelines are the ones that need sourcing. Dots flag each
        criterion on its own — red from {cfg.row_flag?.critical ?? 115}% of average, amber from{' '}
        {cfg.row_flag?.watch ?? 100}%. Because the scores are relative they always average to 100 and
        one cluster is always highest: the index says where to help first, not whether the absolute
        workload is acceptable.
        {anyOut && (
          <b>
            {' '}
            Adjusted for missing capacity — taking capacity out of one cluster also moves the others,
            because every score is measured against the shared average.
          </b>
        )}
        {missing.length > 0 && (
          <b> Excluded (no data): {missing.join(', ')} — remaining weights re-normalised.</b>
        )}
      </p>
    </details>
  );
}
